import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  Download,
  Filter,
  Handshake,
  MessageSquare,
  Plus,
  Search,
  Star,
  TrendingUp,
  BadgeCheck,
  PlusCircle,
} from "lucide-react";

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppSelector } from "@/store/hooks";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCurrencies,
  useMyTrades,
  useProfile,
  useTradingPerformance,
  useUserReputationScore,
} from "@/hooks";
import Loader from "@/components/loader/Loader";
import { AdStatus, AdvertiserTier } from "@/interfaces/advertisement";
import P2PGate, { type MerchantRequirements } from "@/components/user/P2PGate";
import { KycStatus } from "@/interfaces/kyc";
import { TierBadge } from "@/components/user/TierBadge";
import { TradeType, type Trade } from "@/interfaces/trade";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";
import { cn, normalizeCurrency } from "@/lib/utils";
import { formatCurrency } from "@/utils/formatCurrency";
import { ErrorState } from "@/components/ErrorState";
import { tradeSocket } from "@/services/tradeSocket";
import TradeCard from "@/components/shared/TradeCard";
import LeaveReviewDialog from "@/components/p2p/merchant/reviews/LeaveReviewDialog";
import { toast } from "sonner";
import MerchantReviews from "@/components/p2p/merchant/MerchantReviews";
import Analytics from "@/components/user/Analytics";
import type { PaymentMethod as PaymentMethodInterface } from "@/interfaces/payment-method";
import PaymentMethodPage from "@/components/user/PaymentMethod";
import MerchantSetting from "@/components/user/MerchantSetting";
import type { MerchantSettings } from "@/interfaces/admin-setting";

export default function UserP2P() {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";
  const navigate = useNavigate();

  const [openReview, setOpenReview] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") ?? "overview";

  const { data: profile, isLoading } = useProfile(userId);

  const { data: tradePerformance } = useTradingPerformance(userId);

  const openEditPage = (id: string) => {
    navigate(`/dashboard/p2p/ads/${id}/edit`);
  };

  const { data: userScore } = useUserReputationScore(userId);

  const {
    data: currencies,
    isLoading: isLoadingCurrencies,
    isError,
  } = useCurrencies();

  const {
    data: myTrades,
    isLoading: isLoadingMyTrades,
    refetch,
  } = useMyTrades(userId as string);

  const stats = [
    {
      title: "Total Volume",
      value: "$" + " " + formatCurrency(userScore?.tradeVolume ?? 0),
      change: "+12.5%",
      icon: TrendingUp,
    },
    {
      title: "Active Trades",
      value: userScore?.activeTradesCount,
      change: "+5.2%",
      icon: Activity,
    },
    {
      title: "Completion Rate",
      value: (userScore?.completionRate ?? 0) + "%",
      change: "+0.8%",
      icon: CheckCircle2,
    },
    // {
    //   title: "Wallet Balance",
    //   value: "$145,000",
    //   change: "+2.4%",
    //   icon: Wallet,
    // },
  ];

  const ads = profile?.advertisements ?? [];
  const trades = myTrades ?? [];

  const [search, setSearch] = useState("");
  const [tradeFilter, setTradeFilter] = useState("all");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profile) return;

    setMerchantSettings({
      merchantName: profile.merchantName || "",
      currencyId: profile.setting?.currency?.id || "",
      floatingMargin: profile.setting?.floatingMargin ?? 0,
      autoCancelMinutes: profile.setting?.autoCancelMinutes ?? 15,
      autoReplyMessage: profile.setting?.autoReplyMessage || "",

      // UI-only settings (keep defaults or derive later)
      requireKyc: true,
      requirePhoneVerification: true,
      autoReplyEnabled: true,
      tradeNotifications: true,
      hideOfflineAds: true,
      maxActiveAds: 10,
    });
  }, [profile]);

  const [merchantSettings, setMerchantSettings] = useState<MerchantSettings>({
    merchantName: profile?.merchantName || "",
    currencyId: profile?.setting?.currencyId || "NGN",
    floatingMargin: 2.5,
    autoCancelMinutes: 15,
    requireKyc: true,
    requirePhoneVerification: true,
    autoReplyEnabled: true,
    autoReplyMessage: "Welcome. Please follow trade instructions carefully.",
    tradeNotifications: true,
    hideOfflineAds: true,
    maxActiveAds: 10,
  });

  const isKycVerified = Boolean(
    profile?.kycSubmissions?.some((kyc) => kyc.status === KycStatus.APPROVED),
  );

  /**
   * Merchant readiness
   *
   * IMPORTANT:
   * These security/payment fields must be returned by the backend
   * profile endpoint. The `as any` here allows this component to remain
   * compatible with your current profile interface until those fields
   * are added to the frontend interface.
   */
  const profileData = profile as
    | (typeof profile & {
        twoFactorEnabled?: boolean;
        isTwoFactorEnabled?: boolean;
        transactionPasswordEnabled?: boolean;
        hasTransactionPassword?: boolean;
        paymentMethods?: PaymentMethodInterface;
        merchantSettingsCompleted?: boolean;
      })
    | undefined;

  const isTwoFactorEnabled = Boolean(
    profileData?.setting?.twoFAEnabled ??
    profileData?.isTwoFactorEnabled ??
    false,
  );

  const hasTransactionPassword = Boolean(
    profileData?.isSecurityCodeVerified ??
    profileData?.hasTransactionPassword ??
    false,
  );

  const hasVerifiedPaymentMethod = Boolean(
    profileData?.paymentMethods?.some(
      (method) => method.active === true,
      //  ||
      //    method.status?.toUpperCase() === "VERIFIED",
    ),
  );

  /**
   * Merchant settings are considered configured when the merchant
   * has a merchant name, a default currency and the required trading
   * configuration.
   */
  const hasMerchantSettings = Boolean(
    profileData?.merchantName ??
    (profileData?.merchantName?.trim() &&
      profileData?.setting?.currencyId &&
      profileData?.setting?.floatingMargin !== undefined &&
      profileData?.setting?.autoCancelMinutes !== undefined),
  );

  const merchantRequirements: MerchantRequirements = {
    kyc: isKycVerified,
    merchantSettings: hasMerchantSettings,
    twoFactor: isTwoFactorEnabled,
    transactionPassword: hasTransactionPassword,
    paymentMethod: hasVerifiedPaymentMethod,
  };

  const completedMerchantRequirements =
    Object.values(merchantRequirements).filter(Boolean).length;

  const merchantReady =
    completedMerchantRequirements === Object.keys(merchantRequirements).length;

  const filteredTrades = useMemo(() => {
    return trades.filter((trade) => {
      const matchesSearch =
        trade.id.toLowerCase().includes(search.toLowerCase()) ||
        trade.seller?.username.toLowerCase().includes(search.toLowerCase()) ||
        trade.buyer?.username.toLowerCase().includes(search.toLowerCase());

      const matchesFilter =
        tradeFilter === "all" ||
        trade.status.toLowerCase() === tradeFilter.toLowerCase();

      return matchesSearch && matchesFilter;
    });
  }, [search, tradeFilter, trades]);

  const filteredAds = useMemo(() => {
    return ads.filter(
      (ad) =>
        ad.id.toLowerCase().includes(search.toLowerCase()) ||
        ad.asset?.token?.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [ads, search]);

  const handleUpdate = async () => {
    const payload = {
      merchantName: merchantSettings.merchantName,
      currencyId: merchantSettings.currencyId,
      floatingMargin: merchantSettings.floatingMargin,
      autoCancelMinutes: merchantSettings.autoCancelMinutes,
      autoReplyMessage: merchantSettings.autoReplyMessage,
    };

    setLoading(true);
    try {
      const res = await makeRequest.put("/user/update-merchant/me", payload);

      if (res) {
        toast.success(
          res.data.message || "Merchant settings updated successfully",
        );

        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      }
      setLoading(false);
    } catch (error) {
      toast.error(
        getErrorMessage(error) || "An error occurred while updating settings",
      );
      console.log(error);
    }
    setLoading(false);
  };

  const handleExport = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Ad ID,Type,Asset,Price,Status\n" +
      ads
        .map((ad) => `${ad.id},${ad.type},${ad.asset},${ad.price},${ad.status}`)
        .join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "p2p_ads_report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Report exported successfully");
  };

  const saveMerchantSettings = () => {
    toast.success("Merchant settings updated");
  };

  const handleNavigate = (tradeId: string) => {
    navigate(`/p2p/${tradeId}`);
    tradeSocket.connect();
    tradeSocket.joinTrade(tradeId);
  };

  if (isLoading || isLoadingCurrencies || isLoadingMyTrades) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  if (!merchantReady) {
    return <P2PGate requirements={merchantRequirements} userId={userId} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground mt-10">
      <main className="flex-1 sm:p-6 lg:p-8">
        {/* HEADER */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between mb-8 p-2">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Handshake className="w-8 h-8 text-primary" />
              P2P Merchant Center
            </h1>

            <div className="flex items-center gap-3 mt-1">
              <TierBadge tier={profile?.advertiserTier as AdvertiserTier} />

              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Star className="w-3.5 h-3.5 text-yellow-500" />
                {profile?.reputationScore}% Trust Score
              </div>
            </div>

            {/* <p className="text-muted-foreground mt-1">
              Elite Trader • 98.7% Trust Score
            </p> */}
          </div>

          <div className="flex justify-between gap-3">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export Reports
            </Button>

            <Button onClick={() => navigate("/dashboard/ad-form")}>
              <Plus className="w-4 h-4 mr-2" />
              Create Ad
            </Button>
          </div>
        </div>

        <Tabs
          defaultValue="overview"
          className="space-y-6 mx-1"
          value={tab}
          onValueChange={(value) => setSearchParams({ tab: value })}
        >
          <div className="relative mb-6">
            {/* Left Fade */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />

            {/* Right Fade */}
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />

            <div className="overflow-x-auto scrollbar-hide w-full">
              <TabsList className="w-ful justify-start bg-transparent p-0 h-auto overflow-x-auto whitespace-nowrap scrollbar-hide rounded-none">
                <div className="flex gap-2 min-w-max pb-2">
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="overview"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="settings"
                  >
                    Merchant Settings
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="payments"
                  >
                    Payment Methods
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="ads"
                  >
                    My Ads
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="trades"
                  >
                    Trades
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="reviews"
                  >
                    Reviews
                  </TabsTrigger>
                  <TabsTrigger
                    className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                    value="analytics"
                  >
                    Analytics
                  </TabsTrigger>
                  {/* <TabsTrigger value="security">Security</TabsTrigger> */}
                </div>
              </TabsList>
            </div>
          </div>

          <TabsContent value="overview" className="space-y-8">
            {/* QUICK REQUIREMENTS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        KYC Status
                      </p>
                      <h3 className="font-bold text-lg mt-1">Verified</h3>
                    </div>

                    <BadgeCheck className="text-emerald-500" />
                  </div>
                </CardContent>
              </Card>

              {/* <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Linked Accounts
                      </p>
                      <h3 className="font-bold text-lg mt-1">
                        {verifiedAccounts}
                      </h3>
                    </div>

                    <Building2 className="text-primary" />
                  </div>
                </CardContent>
              </Card> */}

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Fiat Currency
                      </p>

                      <h3 className="font-bold text-lg mt-1 flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                          {profile?.setting?.currency?.symbol}
                        </span>

                        <span>{profile?.setting?.currency?.iso}</span>
                      </h3>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Active Ads
                      </p>
                      <h3 className="font-bold text-lg mt-1">
                        {
                          profile?.advertisements.filter(
                            (ad) => ad.status === AdStatus.ACTIVE,
                          ).length
                        }
                      </h3>
                    </div>

                    <Activity className="text-primary" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              {stats.map((item, i) => (
                <div
                  key={i}
                  className="rounded-2xl border bg-card p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>

                    <span className="text-emerald-500 text-sm font-medium">
                      {item.change}
                    </span>
                  </div>

                  <h3 className="text-3xl font-bold">{item.value}</h3>

                  <p className="text-sm text-muted-foreground mt-1">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>

            {/* REPUTATION + CHART */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="rounded-2xl border bg-card p-6 h-max">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-lg">Reputation</h3>

                  <Star className="w-5 h-5 text-yellow-500" />
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Merchant Level
                    </p>

                    <p className="text-2xl font-bold mt-1">
                      {profile?.advertiserTier}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground">
                          Positive Reviews
                        </p>

                        <p className="text-2xl font-bold mt-2">
                          {userScore?.positiveReviewPercent} %
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground">
                          Repeat Users
                        </p>

                        <p className="text-2xl font-bold mt-2">
                          {" "}
                          {userScore?.repeatCustomersCount}{" "}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              <div className="xl:col-span-2 rounded-2xl border bg-card p-6">
                <div className="flex justify-between mb-6">
                  <div>
                    <h3 className="font-semibold text-lg">
                      Trading Performance
                    </h3>

                    <p className="text-sm text-muted-foreground">
                      7-day volume
                    </p>
                  </div>

                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    Filter
                  </Button>
                </div>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={tradePerformance}>
                      <defs>
                        <linearGradient
                          id="colorVol"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#2563eb"
                            stopOpacity={0.35}
                          />

                          <stop
                            offset="95%"
                            stopColor="#2563eb"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        strokeOpacity={0.3}
                      />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="volume"
                        stroke="#2563eb"
                        fill="url(#colorVol)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* ASSET + AI */}
            {/* <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="rounded-2xl border bg-card p-6">
                <div className="flex justify-between mb-6">
                  <div>
                    <h3 className="font-semibold">Asset Allocation</h3>

                    <p className="text-sm text-muted-foreground">
                      Liquidity distribution
                    </p>
                  </div>

                  <Wallet className="text-primary" />
                </div>

                <div className="h-64">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={distribution}
                        innerRadius={65}
                        outerRadius={95}
                        dataKey="value"
                      >
                        {distribution.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>

                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-6 space-y-3">
                  {distribution.map((asset, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: asset.color,
                          }}
                        />

                        <span>{asset.name}</span>
                      </div>

                      <span className="font-medium">{asset.value}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-6 xl:col-span-2 h-max">
                <div className="flex justify-between mb-6">
                  <div>
                    <h3 className="font-semibold">AI Insights</h3>

                    <p className="text-sm text-muted-foreground">
                      Smart recommendations
                    </p>
                  </div>

                  <Bot className="text-primary" />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    "USDT ads perform 28% better after 6PM",
                    "Consider tightening spread by 0.3%",
                    "Opay is preferred for trades under ₦100k",
                  ].map((insight, i) => (
                    <Card key={i} className="p-5">
                      <Bot className="text-primary mb-3" />

                      <p className="text-sm leading-relaxed">{insight}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </div> */}
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="-m-4">
            <MerchantSetting userId={userId} />
          </TabsContent>

          {/* PAYMENT METHODS */}
          <TabsContent value="payments">
            <PaymentMethodPage />
          </TabsContent>

          {/* ADS */}
          <TabsContent value="ads" className="space-y-6">
            <div className="rounded-3xl border bg-card p-2 shadow-sm">
              {/* Header */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">
                    My Advertisements
                  </h2>

                  <p className="text-muted-foreground mt-1">
                    Manage your P2P offers • {filteredAds.length} active
                    listings
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Button
                    variant="outline"
                    onClick={handleExport}
                    className="sm:w-auto w-full h-11"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>

                  <Button
                    onClick={() => navigate("/dashboard/ad-form")}
                    className="sm:w-auto w-full h-11"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Create Ad
                  </Button>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                <div className="rounded-2xl border bg-muted/40 p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    TOTAL ADS
                  </p>
                  <p className="text-3xl font-semibold mt-1">{ads.length}</p>
                </div>
                <div className="rounded-2xl border bg-muted/40 p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    ACTIVE
                  </p>
                  <p className="text-3xl font-semibold mt-1 text-emerald-500">
                    {ads.filter((ad) => ad.status === AdStatus.ACTIVE).length}
                  </p>
                </div>
                <div className="rounded-2xl border bg-muted/40 p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    COMPLETION RATE
                  </p>
                  <p className="text-3xl font-semibold mt-1">
                    {userScore?.completionRate}%
                  </p>
                </div>
                <div className="rounded-2xl border bg-muted/40 p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    THIS MONTH
                  </p>
                  <p className="text-3xl font-semibold mt-1">
                    {userScore?.completedTrades30d} Trades
                  </p>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                <Input
                  className="pl-11 h-12"
                  placeholder="Search advertisements..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Table */}
              <div className="hidden md:block rounded-2xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1150px]">
                    <thead className="sticky top-0 bg-background z-10">
                      <tr className="transition-colors hover:bg-muted/40">
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          ID
                        </th>
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          Type
                        </th>
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          Asset
                        </th>
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          Price Type
                        </th>
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          Price
                        </th>
                        <th className="py-4 px-6 text-left font-medium text-sm">
                          Limits
                        </th>
                        <th className="py-4 px-6 text-center font-medium text-sm">
                          Trades
                        </th>
                        <th className="py-4 px-6 text-center font-medium text-sm">
                          Completion
                        </th>
                        <th className="py-4 px-6 text-center font-medium text-sm">
                          KYC
                        </th>
                        <th className="py-4 px-6 text-center font-medium text-sm">
                          Status
                        </th>
                        <th className="py-4 px-6 text-right font-medium text-sm pr-8">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAds.length > 0 ? (
                        filteredAds.map((ad) => (
                          <tr
                            key={ad.id}
                            className="border-b hover:bg-muted/30 transition-colors group"
                          >
                            <td className="py-5 px-6 font-mono text-sm">
                              {ad.id.slice(0, 8)}...
                            </td>

                            <td className="px-6">
                              <Badge
                                className={cn(
                                  "font-medium",
                                  ad.type === TradeType.SELL
                                    ? "bg-red-500"
                                    : "bg-emerald-500",
                                )}
                              >
                                {ad.type}
                              </Badge>
                            </td>

                            <td className="px-6 font-medium">
                              {normalizeCurrency(ad.asset?.currency ?? "")}
                            </td>

                            <td className="px-6 text-sm text-muted-foreground">
                              {ad.priceType}
                            </td>

                            <td className="px-6 font-medium">
                              {ad.fiatCurrency?.iso}{" "}
                              {Number(ad.price || 0).toFixed(2)}
                            </td>

                            <td className="px-6 text-sm">
                              {ad.fiatCurrency?.iso}{" "}
                              {formatCurrency(
                                Number(ad.minAmount) * Number(ad.price),
                              )}{" "}
                              — {ad.fiatCurrency?.iso}
                              {formatCurrency(
                                Number(ad.maxAmount) * Number(ad.price),
                              )}
                            </td>

                            <td className="px-6 text-center font-medium">
                              {ad.trades?.length || 0}
                            </td>

                            <td className="px-6 text-center">
                              <div className="inline-flex items-center gap-1 text-emerald-500">
                                <span>98.4%</span>
                              </div>
                            </td>

                            <td className="px-6 text-center">
                              <Badge variant="secondary" className="text-xs">
                                Required
                              </Badge>
                            </td>

                            <td className="px-6 text-center">
                              <Badge
                                className={cn(
                                  "capitalize",
                                  ad.type === TradeType.SELL
                                    ? "bg-red-500"
                                    : "bg-emerald-500",
                                )}
                              >
                                {ad.status.toLowerCase()}
                              </Badge>
                            </td>

                            <td className="px-6 text-right pr-8">
                              <div className="flex justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditPage(ad.id)}
                                >
                                  Manage
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="py-20 text-center">
                            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                              <Handshake className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <p className="text-lg font-medium">
                              No advertisements found
                            </p>
                            <p className="text-muted-foreground mt-1">
                              Create your first ad to get started
                            </p>
                            <Button
                              onClick={() => navigate("/dashboard/ad-form")}
                              className="mt-4"
                            >
                              Create Advertisement
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="md:hidden space-y-4">
                {filteredAds.map((ad) => (
                  <Card key={ad.id} className="rounded-2xl">
                    <CardContent className="p-2 space-y-4">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-semibold">
                            {normalizeCurrency(ad.asset?.currency ?? "")}
                          </p>

                          <p className="text-xs text-muted-foreground font-mono">
                            {ad.id.slice(0, 8)}...
                          </p>
                        </div>

                        <Button
                          className={cn(
                            "rounded-full",
                            ad.type === TradeType.SELL && "bg-red-500",
                            ad.type === TradeType.BUY && "bg-emerald-500",
                          )}
                        >
                          {ad.type}
                        </Button>
                      </div>

                      <div className="flex flex-col gap-5 text-sm">
                        <div className="flex justify-between gap-4">
                          <div>
                            <p className="text-muted-foreground">Price</p>

                            <p className="font-medium">
                              {ad.fiatCurrency?.iso}{" "}
                              {Number(ad.price).toFixed(2)}
                            </p>
                          </div>

                          <div>
                            <p className="text-muted-foreground">Trades</p>

                            <p className="font-medium">
                              {ad.trades?.length ?? 0}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-muted-foreground">Status</p>

                            <Badge
                              className={cn(
                                ad.type === TradeType.SELL
                                  ? "bg-red-500"
                                  : "bg-emerald-500",
                              )}
                            >
                              {ad.status}
                            </Badge>
                          </div>

                          <div>
                            <p className="text-muted-foreground">Price Type</p>

                            <p>{ad.priceType}</p>
                          </div>
                        </div>
                        <Button
                          className={cn(
                            "h-12",
                            ad.type === TradeType.SELL
                              ? "bg-red-500"
                              : "bg-emerald-500",
                          )}
                          onClick={() => openEditPage(ad.id)}
                        >
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* TRADES */}
          <TabsContent value="trades">
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-lg">Live Trades</h3>

                  <p className="text-sm text-muted-foreground">
                    Recent activity
                  </p>
                </div>

                <div className="flex gap-2">
                  <ArrowDownLeft className="text-emerald-500" />

                  <ArrowUpRight className="text-red-500" />
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-3 mb-6">
                <div className="relative flex-1 items-center gap-1">
                  <Search className="absolute h-6 w-6 left-3 top-3 text-muted-foreground" />

                  <Input
                    placeholder="Search trades..."
                    className="pl-10 h-12"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <Select value={tradeFilter} onValueChange={setTradeFilter}>
                  <SelectTrigger className="w-full lg:w-40 !h-12">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="EXPIRED">Expired</SelectItem>
                    <SelectItem value="PAID">Paid</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    <SelectItem value="DISPUTED">Disputed</SelectItem>
                    <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                    <SelectItem value="REFUNDED">Refunded</SelectItem>
                    <SelectItem value="RELEASED">Released</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 -mx-4">
                {filteredTrades.map((trade) => (
                  <TradeCard
                    key={trade.id}
                    trade={trade}
                    selectedTrade={selectedTrade}
                    setSelectedTrade={setSelectedTrade}
                    openReview={openReview}
                    setOpenReview={setOpenReview}
                  />
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="space-y-8">
            {/* HEADER */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Reputation & Reviews
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Trust metrics, user feedback, and trade reliability insights
                </p>
              </div>

              {/* <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>

                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div> */}
            </div>

            {/* TOP METRICS */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        TRUST SCORE
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {userScore?.reputationScore ?? 0} %
                      </p>
                    </div>
                    <BadgeCheck className="text-emerald-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        POSITIVE REVIEWS
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {" "}
                        {userScore?.positiveReviewPercent}%{" "}
                      </p>
                    </div>
                    <TrendingUp className="text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        REPEAT USERS
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {" "}
                        {userScore?.repeatCustomersCount}{" "}
                      </p>
                    </div>
                    <Handshake className="text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        DISPUTE RATE
                      </p>
                      <p className="text-2xl font-bold mt-1 text-amber-500">
                        {userScore?.disputeRate ?? 0} %
                      </p>
                    </div>
                    <AlertTriangle className="text-amber-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SENTIMENT + INSIGHTS */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* SENTIMENT */}
              <div className="rounded-2xl border bg-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold">Sentiment Breakdown</h4>
                  <Activity className="text-primary w-5 h-5" />
                </div>

                <div className="space-y-4">
                  {[
                    { label: "Excellent", value: 72, color: "bg-emerald-500" },
                    { label: "Good", value: 18, color: "bg-blue-500" },
                    { label: "Neutral", value: 7, color: "bg-amber-500" },
                    { label: "Poor", value: 3, color: "bg-red-500" },
                  ].map((s) => (
                    <div key={s.label} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{s.label}</span>
                        <span className="text-muted-foreground">
                          {s.value}%
                        </span>
                      </div>

                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full ${s.color}`}
                          style={{ width: `${s.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* INSIGHTS */}
              <div className="xl:col-span-2 rounded-2xl border bg-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold">AI Reputation Insights</h4>
                  <Bot className="text-primary w-5 h-5" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "Fast response traders receive 32% more repeat trades",
                    "USDT sellers have highest trust conversion rate",
                    "Dispute handling time improves rating significantly",
                    "Weekend trades show slightly higher dispute risk",
                  ].map((insight, i) => (
                    <Card
                      key={i}
                      className="p-5 hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Bot className="w-4 h-4 text-primary mt-1" />
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {insight}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* REVIEWS LIST */}
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-semibold">Recent Reviews</h4>
              </div>
              <MerchantReviews merchantId={userId} />
            </div>

            {/* FOOTER ACTION */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Reviews are automatically verified from completed trades
              </p>

              <Button>
                <MessageSquare className="w-4 h-4 mr-2" />
                View All Reviews
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-8">
            <Analytics />
          </TabsContent>

          {/* SECURITY */}
          {/* <TabsContent value="security">
            <div className="rounded-2xl border bg-card p-6 h-max">
              <div className="flex justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-lg">Security Center</h3>

                  <p className="text-sm text-muted-foreground">
                    Protection & monitoring
                  </p>
                </div>

                <Shield className="text-primary" />
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: Lock,
                    label: "2FA Enabled",
                  },
                  {
                    icon: Globe,
                    label: "Withdrawal whitelist active",
                  },
                  {
                    icon: AlertTriangle,
                    label: "No suspicious activity",
                  },
                  {
                    icon: Clock3,
                    label: "Last login • 5 mins ago",
                  },
                  {
                    icon: TimerReset,
                    label: "Auto dispute timeout enabled",
                  },
                  {
                    icon: CreditCard,
                    label: "Payment verification active",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between border rounded-xl p-4"
                  >
                    <div className="flex items-center gap-4">
                      <item.icon className="w-5 h-5 text-primary" />

                      <span>{item.label}</span>
                    </div>

                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              {[
                {
                  label: "Require KYC",
                  value: merchantSettings.requireKyc,
                  key: "requireKyc",
                  icon: BadgeCheck,
                },
                {
                  label: "Require Phone Verification",
                  value: merchantSettings.requirePhoneVerification,
                  key: "requirePhoneVerification",
                  icon: Shield,
                },
                {
                  label: "Enable Auto Reply",
                  value: merchantSettings.autoReplyEnabled,
                  key: "autoReplyEnabled",
                  icon: Bot,
                },
                {
                  label: "Trade Notifications",
                  value: merchantSettings.tradeNotifications,
                  key: "tradeNotifications",
                  icon: BellRing,
                },
                {
                  label: "Hide Offline Ads",
                  value: merchantSettings.hideOfflineAds,
                  key: "hideOfflineAds",
                  icon: Eye,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="border rounded-2xl p-5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>

                    <div>
                      <p className="font-medium">{item.label}</p>

                      <p className="text-sm text-muted-foreground">
                        Configure merchant preference
                      </p>
                    </div>
                  </div>

                  <Switch
                    checked={item.value}
                    onCheckedChange={(checked) =>
                      setMerchantSettings((prev: any) => ({
                        ...prev,
                        [item.key]: checked,
                      }))
                    }
                  />
                </div>
              ))}

              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <Percent className="text-primary" />

                    <div>
                      <h4 className="font-semibold">Trading Fee Tier</h4>

                      <p className="text-sm text-muted-foreground mt-1">
                        Current fee rate: 0.15%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button className="w-full" onClick={saveMerchantSettings}>
                <Save className="mr-2 w-4 h-4" />
                Save Settings
              </Button>
            </div>
          </TabsContent> */}
        </Tabs>
      </main>
      <LeaveReviewDialog
        trade={selectedTrade}
        open={openReview}
        onOpenChange={setOpenReview}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
